import { Component, inject, OnInit } from '@angular/core';
import { getCompanyRequestFormObject } from '../utils/company-request-form-object';
import { CompanyService } from '../../company/company.service';
import { CompanyRepository } from '@/store/company-state/company-state.repository';
import { RefParamsService } from '../../ref-params/ref-params.service';
import { MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { PageHeaderComponent } from '@/components/shared/page-header/page-header.component';
import { FormBuilderComponent } from '@/components/form-builder/form-builder.component';
import { CompanyDetails } from '@/types';
import { Button } from 'primeng/button';
import { Tabs, TabList, Tab, TabPanels, TabPanel } from 'primeng/tabs';
import { ActivityComponent } from '../../activity/activity.component';
import { CompanyMembersComponent } from '@/components/shared/company-members/company-members.component';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { DrawerComponent } from '@/components/drawer/drawer.component';
import { DrawerObject } from '@/components/drawer/drawer.types';
import { BehaviorSubject, Observable } from 'rxjs';
import { FileRepresentation } from '@/components/form-builder/form-builder.types';
import { PreviewPdfComponent } from '@/components/shared/preview-pdf/preview-pdf.component';

@Component({
  selector: 'sm-treat-company-request',
  imports: [
    PageHeaderComponent,
    FormBuilderComponent,
    Button,
    Tabs,
    TabList,
    Tab,
    TabPanels,
    TabPanel,
    ActivityComponent,
    TranslateModule,
    CompanyMembersComponent,
    CommonModule,
    DrawerComponent,
  ],
  templateUrl: './treat-company-request.component.html',
  styleUrl: './treat-company-request.component.scss',
})
export class TreatCompanyRequestComponent implements OnInit {
  companyService = inject(CompanyService);
  companyRepository = inject(CompanyRepository);
  refParamsService = inject(RefParamsService);
  messageService = inject(MessageService);
  activatedRoute = inject(ActivatedRoute);
  translate = inject(TranslateService);
  router = inject(Router);
  sanitizer = inject(DomSanitizer);

  breadcrumb = [
    {
      label: 'settings.companyRequests.breadcrumb.parametres',
      routerLink: ['/settings'],
    },
    {
      label: 'settings.companyRequests.breadcrumb.requestsList',
      routerLink: ['/settings', 'requests-list'],
    },
    {
      label: 'settings.companyRequests.breadcrumb.consult',
      routerLink: ['/settings', 'company', 'treat'],
    },
  ];

  selectedTab = '0';
  companyDocs$ = new BehaviorSubject<FileRepresentation[]>([]);
  
  // Drawer configuration
  drawerObj: DrawerObject = {
    title: 'Document',
    description: '',
    position: 'right',
    width: '40vw',
    dismissable: true,
    closeOnEscape: true,
  };
  showDrawer = false;
  selectedDocUrl$ = new BehaviorSubject<string>('');

  id = 0;
  company!: CompanyDetails;
  companiesTypesOptions: { name: string; code: number }[] = [];
  companyCategoriesOptions: { name: string; code: number }[] = [];
  companyCountriesOptions: { name: string; code: number }[] = [];

  formObject: any = null;

  ngOnInit(): void {
    this.initRouting();
    this.companyService.getOneCompany(this.id).subscribe({
      next: response => {
        this.company = response;
        this.companyRepository.set('dto', {
          nom: response.nom,
          typeId: response.type?.id,
          nif: response.nif,
          dateNif: response.dateNif,
          numCnss: response.numCnss,
          address: response.address,
          categorieSoumissionnaireId: response.categorieSoumissionnaire?.id,
        });
        this.companyRepository.set('companyTypeSelected', {
          name: response.type?.code,
          code: response.type?.id,
        });
        this.companyRepository.set('companyCountrySelected', {
          name: response.address.pays.description,
          code: response.address.pays.id,
        });
        this.companyRepository.set('companyCategorySelected', {
          name: response.categorieSoumissionnaire?.code,
          code: response.categorieSoumissionnaire?.id,
        });
        this.companiesTypesOptions = [{ name: response.type?.code, code: response.type?.id }];
        this.companyCategoriesOptions = [{ name: response.categorieSoumissionnaire?.code, code: response.categorieSoumissionnaire?.id }];
        this.companyCountriesOptions = [{ name: response.address.pays.description, code: response.address.pays.id }];

        this.formObject = getCompanyRequestFormObject({
          store: this.companyRepository,
          companiesTypesOptions: this.companiesTypesOptions,
          companyCategoriesOptions: this.companyCategoriesOptions,
          companyCountriesOptions: this.companyCountriesOptions,
        });

        if (response.societeDocs) {
          const docs: FileRepresentation[] = [];
          if (response.societeDocs.rcDoc) {
            docs.push({
              alfresco_id: response.societeDocs.rcDoc.alfresco_id,
              filename: response.societeDocs.rcDoc.name,
              extension: response.societeDocs.rcDoc.fileExtension,
              size: response.societeDocs.rcDoc.fileSize ? parseFloat(response.societeDocs.rcDoc.fileSize) : undefined,
            });
          }
          if (response.societeDocs.nifDoc) {
            docs.push({
              alfresco_id: response.societeDocs.nifDoc.alfresco_id,
              filename: response.societeDocs.nifDoc.name,
              extension: response.societeDocs.nifDoc.fileExtension,
            });
          }
          if (response.societeDocs.cinDoc) {
            docs.push({
              alfresco_id: response.societeDocs.cinDoc.alfresco_id,
              filename: response.societeDocs.cinDoc.name,
              extension: response.societeDocs.cinDoc.fileExtension,
            });
          }
          this.companyDocs$.next(docs);
        }
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.company.message.error.loading'),
        });
      },
    });
  }

  initRouting() {
    this.id = Number(this.activatedRoute.snapshot.paramMap.get('id'));
  }

  onTabChange(event: any) {
    this.selectedTab = event;
    this.router.navigate([], {
      fragment: event,
      replaceUrl: true,
    });
  }

  validateCompany(isValid: boolean) {
    this.companyService.validateCompany(this.id, isValid).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: isValid
            ? this.translate.instant('settings.companyRequests.message.success.validate')
            : this.translate.instant('settings.companyRequests.message.success.reject'),
        });
        this.router.navigate(['settings', 'requests-list']);
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.companyRequests.message.error.validate'),
        });
      },
    });
  }

  openDoc = (file: FileRepresentation) => {
    if (file.alfresco_id) {
      this.drawerObj.title = file.filename || 'Document';
      this.companyService.downloadCompanyDocs(file.alfresco_id).subscribe({
        next: (res) => {
          if (!res.file) return;
          const byteCharacters = atob(res.file);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const mimeType = file.extension === 'pdf' ? 'application/pdf' : `image/${file.extension}`;
          const blob = new Blob([byteArray], { type: mimeType });
          const url = window.URL.createObjectURL(blob);
          this.selectedDocUrl$.next(url);
          this.drawerObj.component = {
            outlet: PreviewPdfComponent,
            props: {
              src: this.selectedDocUrl$,
              isPdf: file.extension === 'pdf' || mimeType === 'application/pdf',
              height: '80vh'
            }
          };
          this.showDrawer = false;
          setTimeout(() => {
            this.showDrawer = true;
          }, 0);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: 'Erreur lors du chargement du document',
          });
        },
      });
    }
  };
}
